import { useHammer, UseHammerEventCallback } from '@/features/shared/Hooks/interactions/useHammer';
import { useSticky } from '@/features/shared/Hooks/sticky/useSticky';
import useIsMobile from '@/features/shared/Hooks/useIsMobile';
import { MuiContainer, MuiTab, MuiTabs } from '@/features/shared/material-ui';
import clsx from 'clsx';
import React, { memo } from 'react';
import { Fade } from 'react-reveal';
import { TabbedCardProps } from './TabbedCard.interfaces';
import { useTabbedCardLogic } from './TabbedCard.logic';
import { useTabbedCardStyles } from './TabbedCard.styles';
import { TabbedCardTab } from './TabbedCardTab';
import { TabbedCardTabPanel } from './TabbedCardTabPanel';

const a11yProps = (index: any) => {
  return {
    id: `tabbed-card-tab-${index}`,
    'aria-controls': `tabbed-card-tabpanel-${index}`,
  };
};

const TabbedCard = ({
  contentButtons,
  tabs,
  theme,
}: Br.PropsWithContentButtons<TabbedCardProps>) => {
  const classes = useTabbedCardStyles({ theme });
  const isMobile = useIsMobile();
  const { stickyRef } = useSticky(isMobile, { zIndex: '1099', top: '58px' });
  const { onSwipe, currentTabIndex, onTabChangeClicking, contentViewRef } =
    useTabbedCardLogic(tabs);
  const { elementRef } = useHammer('swipe', onSwipe as UseHammerEventCallback, isMobile);

  return (
    <div className={classes.root} data-component-variant="tabbed-card">
      {contentButtons?.content}
      <div ref={stickyRef} className={classes.tabsWrapper}>
        <MuiContainer>
          {!!tabs?.length && (
            <MuiTabs
              orientation="horizontal"
              value={currentTabIndex}
              onChange={(_: any, selected: number) => onTabChangeClicking(selected)}
              className={classes.tabs}
              variant={isMobile ? 'scrollable' : 'standard'}
              scrollButtons={isMobile ? 'on' : 'off'}
            >
              {tabs.map(({ tabTitle }, i) => (
                <MuiTab
                  // eslint-disable-next-line react/no-array-index-key
                  key={`tab_${i}`}
                  title={tabTitle}
                  theme={theme}
                  component={TabbedCardTab}
                  className={clsx(classes.tab, 'gtm-tab')}
                  {...a11yProps(i)}
                />
              ))}
            </MuiTabs>
          )}
        </MuiContainer>
      </div>
      <div ref={contentViewRef}>
        <div ref={elementRef} className={classes.tabPanelsWrapper}>
          {!!tabs?.length && (
            <MuiContainer>
              {tabs?.map((tab, index) => (
                <Fade key={`tab_fade_${index}`}>
                  <div hidden={currentTabIndex !== index} data-tab-index={index}>
                    <TabbedCardTabPanel tab={tab} theme={theme} />
                  </div>
                </Fade>
              ))}
            </MuiContainer>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(TabbedCard);
